import { Alert, Button, Card, CardHeader, CardTitle, DialogRoot, DialogTrigger, ErrorLabel, Input } from '@ibsys/kit';
import { Settings } from 'lucide-react';
import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/shared/hooks';
import { IconButton } from '@/shared/ui';

import { SettingsDialog } from './SettingsDialog';

type AuthPageProps = {
  onClickSignWithGoogle?: () => void;
  onSuccessLogin?: (email: string) => void;
};

export const AuthPage: React.FC<AuthPageProps> = ({ onSuccessLogin }) => {
  const { t } = useTranslation(['auth']);
  const { login, isLoading, error, isSessionExpired, resetError } = useAuth();

  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleClickLogin = async () => {
    if (password && email) {
      try {
        await login({ email, password });

        if (onSuccessLogin) {
          onSuccessLogin(email);
        }
      } catch {
        // Ошибка обрабатывается в auth store
      }
    }
  };

  // Сбрасываем ошибку при размонтировании компонента
  useEffect(() => {
    return () => {
      resetError();
    };
  }, [resetError]);

  return (
    <div className="flex w-full h-dvh bg-[url('/public/images/auth-background.webp')] bg-no-repeat bg-cover">
      <DialogRoot open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <div className="absolute bottom-4 left-4 z-10 flex items-center justify-center rounded-lg border border-border bg-background/80 p-1 shadow-sm backdrop-blur-sm">
          <IconButton
            size="icon"
            label={t('settings')}
            icon={<Settings className="h-5 w-5" />}
            trigger={DialogTrigger}
            onClick={() => setIsSettingsOpen(true)}
          />
        </div>

        <SettingsDialog isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      </DialogRoot>

      <div className={'flex-1 relative'}>
        <Card className="absolute top-[50%] px-4 py-[58px] left-[50%] w-full max-w-sm transform-[translate(-50%,-50%)] rounded-3xl">
          <CardHeader className={'border-none flex'}>
            <CardTitle className={'mx-auto text-xl'}>{t('title')}</CardTitle>
          </CardHeader>
          {isSessionExpired && (
            <Alert
              type="warning"
              className="mb-6"
              title={t('sessionExpired.title')}
              description={t('sessionExpired.description')}
            />
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  required
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="grid gap-2">
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  id="password"
                  type="password"
                  required
                  label={
                    <div className="flex items-center">
                      <span>{t('password')}</span>
                    </div>
                  }
                  disabled={isLoading}
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12"
                disabled={!password || !email || isLoading}
                onClick={handleClickLogin}
                isLoading={isLoading}
                style={
                  {
                    ['--color-primary']: '#1C252E',
                  } as CSSProperties
                }
              >
                {t('signIn')}
              </Button>
              {error && <ErrorLabel className={'text-center'}>{error}</ErrorLabel>}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
